import type { RenditionSize } from '@shared/utils/photoRendition';
import { addWidth, removeRendition } from '@shared/utils/photoRendition';

/**
 * Single source of truth for guest site photo rendition sizes.
 * Used by both OptimizedEventGuestSitePhoto (runtime) and PreloadImages (SSR).
 *
 * LOW_RES is preloaded during SSR and rendered immediately.
 * HIGH_RES is loaded in the background after hydration, then crossfaded in.
 */
export const LOW_RES_RENDITION: RenditionSize = 'tiny';
export const HIGH_RES_RENDITION: RenditionSize = 'xlarge';

/**
 * Widths offered as `srcSet` candidates for the sharp/high-res photo layer.
 *
 * These are requested as explicit pixel widths, not named renditions. A named rendition sizes the
 * photo's *shorter* dimension, so its width varies with each photo's aspect ratio — `medium` is
 * 1440w on a 3:2 landscape but 1280w on a 4:3 and 960w on a portrait. A `w` descriptor has to
 * state a real width, so named buckets cannot express one correctly.
 *
 * The ladder covers a phone at 2x through a desktop hero at 2x. Kept deliberately short: every
 * distinct width is its own cache entry in a transcode-on-read service.
 */
export const HIGH_RES_SRCSET_WIDTHS_PX: ReadonlyArray<number> = [640, 960, 1280, 1600, 2048];

/**
 * Builds a `srcSet` string (e.g. `"url?width=640 640w, url?width=960 960w, ..."`) for the given
 * photo URL.
 *
 * This is the single place that decides which candidates exist for the high-res layer, so the
 * on-page `<img>` (OptimizedEventGuestSitePhoto) and its internal cache-warming preloader
 * (useProgressiveImage) always offer the browser the exact same candidate list — see
 * useProgressiveImage.ts's `containerWidthPx` param for why that consistency matters.
 *
 * Any incoming `rendition` is stripped first. The photo service resolves a rendition *before* it
 * looks at width, so a URL carrying both silently ignores the width and serves a differently-sized
 * image than the `w` descriptor promises — the exact mis-selection this candidate list exists to fix.
 */
export const buildHighResSrcSet = (photoUrl: string): string => {
  const base = removeRendition(photoUrl);
  return HIGH_RES_SRCSET_WIDTHS_PX.map(width => `${addWidth(base, width)} ${width}w`).join(', ');
};
